// Shared display formatters. Single source of truth so panels, tables, and
// dialogs render currency / dates the same way across the app.
//
// Conventions:
//   - Missing values render as "—" (em-dash). $0 is real data, not the same
//     as "no data on file" — keep them visually distinct.
//   - Currency defaults to 2 decimals. Premium tiles on the Agency Dashboard
//     "Top Written Premium" tab use `formatWholeDollars` for an at-a-glance
//     read; everywhere else uses the default.

const EMDASH = "—";

const usdCurrency = (decimals: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const usdCurrency2 = usdCurrency(2);
const usdCurrency0 = usdCurrency(0);

/** Format a USD currency value with 2 decimal places. Returns "—" for null /
 *  undefined / NaN / Infinity so missing or undefined data is visually distinct
 *  from $0. */
export function formatCurrency(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return EMDASH;
  return usdCurrency2.format(value);
}

/** Format a USD currency value with no decimal places. Use for at-a-glance
 *  premium tiles where 2-decimal precision adds visual noise without informing
 *  the user. Returns "—" for null / undefined / NaN / Infinity. */
export function formatWholeDollars(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return EMDASH;
  return usdCurrency0.format(value);
}

/** Format a percentage value. Input is the percentage itself (e.g. 5.5 → "5.5%"),
 *  not a decimal fraction. One decimal place; trailing ".0" trimmed for whole
 *  numbers. Returns "—" for null / undefined / NaN / Infinity. */
export function formatPercentage(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return EMDASH;
  const rounded = Math.round(value * 10) / 10;
  return `${rounded}%`;
}

/** Format a date for display. Accepts Date instances and ISO strings (with or
 *  without time / timezone). Returns "—" for null / undefined / unparseable
 *  inputs so missing data renders consistently with currency. */
export function formatDate(value: Date | string | null | undefined): string {
  if (value == null) return EMDASH;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return EMDASH;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Convert a Date to a YYYY-MM-DD string in the user's LOCAL timezone.
 *
 *  This exists because `Date.prototype.toISOString().slice(0, 10)` returns the
 *  UTC date — which silently drifts off by one day for users west of UTC late
 *  in the evening. Loss dates and policy effective/expiration dates are stored
 *  as bare YYYY-MM-DD strings without timezone info, so they have to be
 *  compared against a same-shape string derived from the user's local clock,
 *  not UTC. Used by the "Last 30 days" quick-filter chip on Claims MRP and
 *  "Renewing in 60 days" on Policy MRP, where the previous UTC-based math
 *  silently dropped a day of results for Pacific-time users after ~7pm.
 */
export function toLocalIsoDate(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Today's date in MM/DD/YYYY (zero-padded). Used as the "Date Run" stamp on
 *  generated PDFs. Caller passes a Date so callers in tests or workers can
 *  inject a clock. */
export function formatMmDdYyyy(value: Date): string {
  const mm = String(value.getMonth() + 1).padStart(2, "0");
  const dd = String(value.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${value.getFullYear()}`;
}

/** Format a date in MEMIC's short M/D/YYYY style (e.g. "7/14/2026"). Used by
 *  the Loss Ratio panel's term-range header and other tight-space tiles where
 *  the full month name is too wide. Same null / NaN handling as formatDate. */
export function formatShortDate(value: Date | string | null | undefined): string {
  if (value == null) return EMDASH;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return EMDASH;
  return d.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });
}
